import React from "react";
import {
  Container,
  Typography,
  Grid,
  Box,
  IconButton,
  Button,
  useTheme,
  useMediaQuery
} from "@mui/material";
import { motion } from "framer-motion";
import {
  LinkedIn,
  Email,
  Rocket,
  ArrowForward
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import brand from "../images/brand.png";

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, duration: 0.6 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const quickLinks = [
    { name: "Services", to: "/services" },
    { name: "Products", to: "/products-tools" },
    { name: "Company", to: "/about" },
    { name: "Contact", to: "/contact" }
  ];

  const exploreLinks = [
    { name: "Products and Tools", to: "/products-tools" },
    { name: "Partnership Program", to: "/partners-service" },
    { name: "Training Programs", to: "/training" }
  ];

  return (
    <Box
      component="footer"
      sx={{
        background: "linear-gradient(135deg, #0a1128 0%, #1a1f3d 100%)",
        borderTop: "1px solid rgba(139, 0, 0, 0.3)",
        py: 6,
        position: "relative",
        overflow: "hidden"
      }}
    >
      {/* Glow */}
      <Box
        sx={{
          position: "absolute",
          top: "20%",
          left: "10%",
          width: 120,
          height: 120,
          background:
            "radial-gradient(circle, rgba(139,0,0,0.08) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(20px)"
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <Grid container spacing={4}>
            {/* Brand */}
            <Grid item xs={12} md={4}>
              <motion.div variants={itemVariants}>
                <Box
                  component="img"
                  src={brand}
                  alt="Zolang Nexora Logo"
                  sx={{ width: { xs: 160, md: 200 }, mb: 2 }}
                />

                <Typography
                  variant="h6"
                  sx={{ color: "rgba(255,255,255,0.9)", mb: 2 }}
                >
                  Zolang Nexora Softwares
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.75)", mb: 3 }}
                >
                  Transforming businesses with innovative digital solutions and
                  cutting-edge technology.
                </Typography>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <IconButton
                    component="a"
                    href="https://linkedin.com/company/zolang-nexora"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      background: "rgba(255,255,255,0.05)",
                      color: "#0077B5",
                      "&:hover": {
                        background: "#0077B5",
                        color: "#fff"
                      }
                    }}
                  >
                    <LinkedIn />
                  </IconButton>

                  <IconButton
                    component="a"
                    href="mailto:contact@zolangnexora.com"
                    sx={{
                      background: "rgba(255,255,255,0.05)",
                      color: "#EA4335",
                      "&:hover": {
                        background: "#EA4335",
                        color: "#fff"
                      }
                    }}
                  >
                    <Email />
                  </IconButton>
                </Box>
              </motion.div>
            </Grid>

            {/* Quick Links */}
            <Grid item xs={12} md={4}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#ff4d4d",
                    mb: 3,
                    display: "flex",
                    alignItems: "center",
                    gap: 1
                  }}
                >
                  <Rocket fontSize="small" /> Quick Links
                </Typography>

                {quickLinks.map((item, i) => (
                  <Button
                    key={i}
                    component={Link}
                    to={item.to}
                    startIcon={<ArrowForward fontSize="small" />}
                    sx={{
                      justifyContent: "flex-start",
                      color: "rgba(255,255,255,0.8)",
                      textTransform: "none",
                      px: 0,
                      "&:hover": {
                        color: "#ff4d4d",
                        background: "transparent"
                      }
                    }}
                  >
                    {item.name}
                  </Button>
                ))}
              </motion.div>
            </Grid>

            {/* Explore More */}
            <Grid item xs={12} md={4}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#ff4d4d",
                    mb: 3,
                    display: "flex",
                    alignItems: "center",
                    gap: 1
                  }}
                >
                  <Rocket fontSize="small" /> Explore More
                </Typography>

                <Grid container spacing={1.5}>
                  {exploreLinks.map((item, i) => (
                    <Grid item xs={12} key={i}>
                      <Box
                        component={Link}
                        to={item.to}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          px: 2,
                          py: 1.5,
                          borderRadius: 2,
                          textDecoration: "none",
                          background:
                            "linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))",
                          border: "1px solid rgba(255,255,255,0.08)",
                          color: "rgba(255,255,255,0.85)",
                          "&:hover": {
                            color: "#ff4d4d",
                            borderColor: "rgba(255,77,77,0.4)",
                            boxShadow:
                              "0 6px 18px rgba(255,77,77,0.25)",
                            transform: "translateX(4px)"
                          }
                        }}
                      >
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <ArrowForward fontSize="small" />
                          {item.name}
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>
            </Grid>
          </Grid>

          {/* Bottom */}
          <Box
            sx={{
              borderTop: "1px solid rgba(139,0,0,0.2)",
              mt: 5,
              pt: 3,
              textAlign: "center"
            }}
          >
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)" }}>
              © {new Date().getFullYear()} Zolang Nexora Softwares •{" "}
              <Link
                to="/terms-conditions"
                style={{ color: "#ff4d4d", textDecoration: "none" }}
              >
                Terms
              </Link>{" "}
              •{" "}
              <Link
                to="/privacy-policy"
                style={{ color: "#ff4d4d", textDecoration: "none" }}
              >
                Privacy
              </Link>
            </Typography>

            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.45)", mt: 1, display: "block" }}
            >
              Privacy-focused • Secure data handling • Transparent policies
            </Typography>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Footer;
